package ai.jessie.web.work

import ai.jessie.web.onboarding.HTTP_FORBIDDEN
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.problem

// UC-008 outcomes. Everything else keeps the UC-006 tenant-denial presentation unchanged.
internal fun WorkRequestFailure.workProblem(saving: Boolean): OnboardingProblem =
    when {
        // A2, A5: no record or reference detail, only the neutral absence.
        status == HTTP_NOT_FOUND -> unavailableWorkProblem(saving)
        // A8: the current entry is shown for a renewed correction.
        detail.code == "STALE_WORK" ->
            OnboardingProblem(
                "Der Arbeitseintrag wurde inzwischen geändert. Es wurde nichts gespeichert. Der " +
                    "aktuelle Stand wird angezeigt; trage gewünschte Änderungen erneut ein."
            )
        // A10.
        saving && status == HTTP_FORBIDDEN ->
            OnboardingProblem(
                "Dein Zugriff hat sich geändert. Es wurde nichts gespeichert und der " +
                    "Arbeitseintrag bleibt unverändert."
            )
        // A3, A9: nothing may be presented as saved.
        saving && status >= HTTP_SERVER_ERROR ->
            OnboardingProblem(
                "Der Arbeitseintrag konnte nicht gespeichert werden. Es wurde nichts " +
                    "gespeichert. Deine Angaben bleiben erhalten; bitte versuche es erneut."
            )
        else -> OnboardingRequestFailure(status, detail.code).problem()
    }

internal fun unavailableWorkProblem(saving: Boolean = false) =
    OnboardingProblem(
        if (saving)
            "Nicht verfügbar oder du hast keinen Zugriff. Es wurde nichts gespeichert. Die " +
                "wählbaren Angaben wurden neu geladen."
        else "Nicht verfügbar oder du hast keinen Zugriff."
    )

// UC-009 outcomes of a calendar read; denials keep the UC-008 and UC-006 presentation.
internal fun WorkRequestFailure.calendarProblem(): OnboardingProblem =
    when {
        detail.code == "INVALID_CALENDAR_RANGE" ->
            OnboardingProblem(
                "Dieser Kalenderzeitraum ist ungültig. Es werden keine Einträge oder Summen " +
                    "angezeigt. Öffne den Kalender über «Aktuelle Woche» erneut."
            )
        // A8: a failed read is never an empty calendar.
        status >= HTTP_SERVER_ERROR -> calendarUnavailableProblem()
        else -> workProblem(saving = false)
    }

internal fun calendarUnavailableProblem() =
    OnboardingProblem(
        "Der Kalender kann zurzeit nicht geladen werden. Es werden keine Einträge oder Summen " +
            "angezeigt. Bitte versuche es erneut."
    )

internal fun workValidationProblem(problems: Map<String, String>) =
    OnboardingProblem(
        "Bitte korrigiere die markierten Felder. Es wurde nichts gespeichert.",
        problems.keys.map { "work-$it" },
    )

internal fun uncertainWorkProblem() =
    OnboardingProblem(
        "Die Antwort konnte nicht bestätigt werden. Bitte brich ab und lade neu, um den " +
            "gespeicherten Stand zu prüfen."
    )

internal fun workFieldLabel(field: String) =
    when (field) {
        "workDate" -> "Datum"
        "durationMinutes" -> "Dauer"
        "memberId" -> "Mitglied"
        "projectId" -> "Projekt"
        "positionId" -> "Position"
        "description" -> "Beschreibung"
        else -> "Angabe"
    }

internal fun workCorrection(field: String, reason: String) =
    when (reason) {
        "FUTURE" -> "Das Datum darf nicht nach heute in der Zeitzone der Organisation liegen."
        "WHOLE_MINUTES_REQUIRED" -> "Gib 1 bis 1440 ganze Minuten ein."
        "TOO_LONG" -> "Höchstens 1000 Zeichen."
        "REQUIRED" -> "Bitte wähle einen Eintrag."
        else -> if (field == "workDate") "Gib ein gültiges Datum ein." else "Bitte korrigieren."
    }

internal const val HTTP_SERVER_ERROR = 500
private const val HTTP_NOT_FOUND = 404
